/**
 * 响应式适配模块
 * 统一管理设备检测、rem 适配、窗口事件监听等功能
 */
import { ref, computed, Ref } from 'vue';
import { detectDevice } from '@/common/ts/utils';

// ==================== 类型定义 ====================
export type DeviceType = 'pc' | 'mobile' | 'tablet' | 'unknown';

type EventCallback = () => void;

interface RemConfig {
  designWidth: number;
  baseFont: number;
  minFont: number;
  maxFont: number;
  pcThreshold: number;
}

// ==================== 事件管理器 ====================
/**
 * 全局事件管理器
 * 用于统一管理 window 事件监听，避免重复监听
 */
class GlobalEventManager {
  private resizeCallbacks: Set<EventCallback> = new Set();
  private orientationCallbacks: Set<EventCallback> = new Set();
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private isResizeListenerAttached = false;
  private isOrientationListenerAttached = false;

  /**
   * 订阅 resize 事件
   * @param callback 回调函数
   * @param debounceDelay 防抖延迟（毫秒），默认 150
   * @returns 取消订阅的函数
   */
  onResize(callback: EventCallback, debounceDelay = 150): () => void {
    this.resizeCallbacks.add(callback);

    if (!this.isResizeListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
      this.isResizeListenerAttached = true;
    }

    return () => {
      this.resizeCallbacks.delete(callback);
      if (this.resizeCallbacks.size === 0 && this.isResizeListenerAttached) {
        window.removeEventListener('resize', this.handleResize);
        this.isResizeListenerAttached = false;
        if (this.resizeTimer) {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = null;
        }
      }
    };
  }

  /**
   * 订阅 orientationchange 事件
   * @param callback 回调函数
   * @returns 取消订阅的函数
   */
  onOrientationChange(callback: EventCallback): () => void {
    this.orientationCallbacks.add(callback);

    if (!this.isOrientationListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('orientationchange', this.handleOrientationChange);
      this.isOrientationListenerAttached = true;
    }

    return () => {
      this.orientationCallbacks.delete(callback);
      if (this.orientationCallbacks.size === 0 && this.isOrientationListenerAttached) {
        window.removeEventListener('orientationchange', this.handleOrientationChange);
        this.isOrientationListenerAttached = false;
      }
    };
  }

  private handleResize = () => {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      this.resizeCallbacks.forEach(callback => callback());
    }, 150);
  };

  private handleOrientationChange = () => {
    setTimeout(() => {
      this.orientationCallbacks.forEach(callback => callback());
    }, 100);
  };
}

// 创建全局单例
const eventManager = new GlobalEventManager();

// ==================== Rem 适配管理器 ====================
/**
 * Rem 适配管理器
 * 根据屏幕宽度动态调整根字体大小
 */
class RemManager {
  private config: RemConfig | null = null;
  private unsubscribeResize: (() => void) | null = null;
  private unsubscribeOrientation: (() => void) | null = null;

  init(options?: Partial<RemConfig>) {
    this.config = Object.assign(
      {
        designWidth: 375,
        baseFont: 16,
        minFont: 12,
        maxFont: 18,
        pcThreshold: 1024,
      },
      options || {}
    );

    this.update();

    // 订阅 resize 事件（使用较短的防抖时间，80ms）
    this.unsubscribeResize = eventManager.onResize(() => {
      this.update();
    }, 80);

    // 订阅 orientationchange 事件
    this.unsubscribeOrientation = eventManager.onOrientationChange(() => {
      this.update();
    });
  }

  private update() {
    if (!this.config || typeof document === 'undefined') return;

    const docEl = document.documentElement;
    let w = docEl.clientWidth || window.innerWidth;
    if (!w) w = this.config.designWidth;

    // 如果是非常大的屏幕（PC），保持一个稳定的根字号
    if (w >= this.config.pcThreshold) {
      docEl.style.fontSize = this.config.baseFont + 'px';
      return;
    }

    // 按比例缩放：当 width == designWidth 时，html font-size == baseFont
    let fs = this.config.baseFont * (w / this.config.designWidth);

    // 边界保护
    fs = Math.max(this.config.minFont, Math.min(this.config.maxFont, fs));

    docEl.style.fontSize = fs + 'px';
  }

  destroy() {
    if (this.unsubscribeResize) {
      this.unsubscribeResize();
      this.unsubscribeResize = null;
    }
    if (this.unsubscribeOrientation) {
      this.unsubscribeOrientation();
      this.unsubscribeOrientation = null;
    }
    this.config = null;
  }
}

const remManager = new RemManager();

/**
 * 初始化 rem
 * @param options 配置选项
 */
export function initRem(options?: Partial<RemConfig>) {
  remManager.init(options);
}

// ==================== 设备检测管理器 ====================
/**
 * 设备检测管理器
 * 全局单例，管理设备类型状态和事件监听
 */
class DeviceDetectionManager {
  private deviceType: Ref<DeviceType>;
  private unsubscribeResize: (() => void) | null = null;
  private unsubscribeOrientation: (() => void) | null = null;

  constructor() {
    this.deviceType = ref(detectDevice());
    this.updateClass();
    this.startListening();
  }

  /**
   * 获取当前设备类型（响应式）
   */
  getDeviceType(): Ref<DeviceType> {
    return this.deviceType;
  }

  /**
   * 更新设备类型
   */
  private updateDevice() {
    this.deviceType.value = detectDevice();
    this.updateClass();
  }

  /**
   * 更新 DOM class
   */
  private updateClass() {
    if (typeof document === 'undefined') return;
    if (this.deviceType.value !== 'pc') {
      document.documentElement.classList.add('mb');
    } else {
      document.documentElement.classList.remove('mb');
    }
  }

  /**
   * 开始监听事件
   */
  private startListening() {
    // 订阅 resize 事件
    this.unsubscribeResize = eventManager.onResize(() => {
      this.updateDevice();
    });

    // 订阅 orientationchange 事件
    this.unsubscribeOrientation = eventManager.onOrientationChange(() => {
      this.updateDevice();
    });
  }

  /**
   * 停止监听事件
   */
  stopListening() {
    if (this.unsubscribeResize) {
      this.unsubscribeResize();
      this.unsubscribeResize = null;
    }
    if (this.unsubscribeOrientation) {
      this.unsubscribeOrientation();
      this.unsubscribeOrientation = null;
    }
  }
}

const deviceManager = new DeviceDetectionManager();

/**
 * 自动监听窗口大小和设备方向变化，返回响应式的设备类型
 * 
 * @example
 * ```ts
 * const { currentDevice } = useDeviceDetection()
 * // currentDevice 是响应式的，会自动更新
 * ```
 */
export function useDeviceDetection() {
  const deviceType = deviceManager.getDeviceType();
  const currentDevice = computed(() => deviceType.value);

  return {
    /** 当前设备类型（响应式） */
    currentDevice,
  };
}
